import React, { useState, useEffect, useRef } from 'react';
import { templateAPI, brandingAPI } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Palette, Lock, Check, Crown, Star, Upload, Save,
  ChevronRight, Building2, Phone, Globe, Mail, MapPin,
  Facebook, Instagram, Twitter, Youtube, Sparkles, Image as ImageIcon,
  X, Eye, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

// Template visual previews (CSS-based mockups)
const TEMPLATE_PREVIEWS = {
  creative_1: { icon: '1', gradient: 'from-blue-500 to-blue-700', tag: 'Promo' },
  creative_2: { icon: '2', gradient: 'from-gray-100 to-gray-300', tag: 'Clean', dark: true },
  creative_3: { icon: '3', gradient: 'from-red-500 to-orange-500', tag: 'Offer' },
  creative_4: { icon: '4', gradient: 'from-green-500 to-emerald-600', tag: 'Split' },
  creative_5: { icon: '5', gradient: 'from-purple-500 to-pink-500', tag: 'Festive' },
  creative_6: { icon: '6', gradient: 'from-blue-400 to-cyan-400', tag: 'Info' },
  creative_7: { icon: '7', gradient: 'from-gray-600 to-gray-800', tag: 'Review' },
  creative_8: { icon: '8', gradient: 'from-indigo-500 to-purple-600', tag: 'Modern' },
  creative_9: { icon: '9', gradient: 'from-yellow-400 to-orange-400', tag: 'Social' },
  creative_10: { icon: '10', gradient: 'from-green-400 to-teal-500', tag: 'Local' },
};

const CreativeTemplatePage = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [maxTemplates, setMaxTemplates] = useState(2);
  const [planName, setPlanName] = useState('free');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('templates'); // templates | branding
  const [branding, setBranding] = useState({});
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewModal, setPreviewModal] = useState(null); // template object or null
  const [previewImage, setPreviewImage] = useState('');
  const [previewGenerating, setPreviewGenerating] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => { fetchTemplates(); fetchBranding(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await templateAPI.getAll();
      setTemplates(res.data.templates || []);
      setSelectedIds(res.data.selected_ids || []);
      setMaxTemplates(res.data.max_templates || 2);
      setPlanName(res.data.plan_name || 'free');
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const fetchBranding = async () => {
    try {
      const res = await brandingAPI.get();
      setBranding(res.data.branding || {});
    } catch { /* ignore */ }
  };

  const handleToggleTemplate = (templateId, isAccessible) => {
    if (!isAccessible) {
      toast.error('Upgrade your plan to access this template');
      return;
    }
    setSelectedIds(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      }
      if (prev.length >= maxTemplates) {
        toast.error(`You can select up to ${maxTemplates} templates. Deselect one first.`);
        return prev;
      }
      return [...prev, templateId];
    });
  };

  const handleSaveSelection = async () => {
    setSaving(true);
    try {
      await templateAPI.select(selectedIds);
      toast.success('Template selection saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save selection');
    } finally { setSaving(false); }
  };

  const handleOpenPreview = (tmpl, isAccessible) => {
    if (!isAccessible) {
      toast.error('Upgrade your plan to preview this template');
      return;
    }
    setPreviewImage('');
    setPreviewModal(tmpl);
  };

  const handleGeneratePreview = async () => {
    if (!previewModal) return;
    setPreviewGenerating(true);
    try {
      const bName = branding.business_name || user?.name || 'My Business';
      const res = await templateAPI.generate({
        template_id: previewModal.id,
        post_content: `Check out ${bName}! We offer the best quality services for our valued customers. Visit us today for exclusive deals.`,
        headline: `Welcome to ${bName}`,
        offer_text: previewModal.category === 'offer' ? 'Special 20% OFF this week!' : '',
      });
      if (res.data.image_url) {
        setPreviewImage(res.data.image_url);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate preview');
    } finally { setPreviewGenerating(false); }
  };

  const handleToggleFromModal = () => {
    if (!previewModal) return;
    handleToggleTemplate(previewModal.id, previewModal.is_accessible);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Select an image file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBranding(prev => ({ ...prev, logo_url: res.data.image_url }));
      toast.success('Logo uploaded!');
    } catch { toast.error('Failed to upload logo'); }
    finally { setUploading(false); }
  };

  const handleSaveBranding = async () => {
    setBrandingLoading(true);
    try {
      await brandingAPI.update(branding);
      toast.success('Branding profile saved!');
    } catch { toast.error('Failed to save branding'); }
    finally { setBrandingLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#9334E9] to-[#1A73E8] rounded-xl flex items-center justify-center">
            <Palette className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Creative Templates</h1>
            <p className="text-[#5F6368]">Select templates and manage your business branding</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-[#F1F3F4] rounded-lg px-3 py-1.5 text-sm">
          <Crown className="w-4 h-4 text-[#F9AB00]" />
          <span className="text-[#5F6368]">
            <strong className="text-[#202124] capitalize">{planName}</strong> — {selectedIds.length}/{maxTemplates} templates
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-card">
        <div className="flex gap-1 bg-[#F1F3F4] rounded-lg p-1">
          <button
            onClick={() => setActiveTab('templates')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'templates' ? 'bg-white text-[#202124] shadow-sm' : 'text-[#5F6368]'
            }`}
            data-testid="tab-templates"
          >
            <Palette className="w-4 h-4 inline mr-1.5" /> Template Library
          </button>
          <button
            onClick={() => setActiveTab('branding')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'branding' ? 'bg-white text-[#202124] shadow-sm' : 'text-[#5F6368]'
            }`}
            data-testid="tab-branding"
          >
            <Building2 className="w-4 h-4 inline mr-1.5" /> Business Branding
          </button>
        </div>
      </div>

      {/* Template Library Tab */}
      {activeTab === 'templates' && (
        <>
          {/* Template Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="template-grid">
            {templates.map((tmpl) => {
              const preview = TEMPLATE_PREVIEWS[tmpl.id] || { icon: '?', gradient: 'from-gray-400 to-gray-600', tag: '' };
              const isSelected = selectedIds.includes(tmpl.id);
              const isAccessible = tmpl.is_accessible;

              return (
                <div
                  key={tmpl.id}
                  className={`relative group rounded-xl overflow-hidden border-2 transition-all duration-200 text-left ${
                    isSelected ? 'border-[#1A73E8] ring-2 ring-[#1A73E8] ring-offset-2' :
                    isAccessible ? 'border-[#DADCE0] hover:border-[#5F6368] hover:shadow-md' :
                    'border-[#DADCE0] opacity-60'
                  }`}
                  data-testid={`template-${tmpl.id}`}
                >
                  {/* Preview area - clickable for preview modal */}
                  <button
                    onClick={() => handleOpenPreview(tmpl, isAccessible)}
                    className={`w-full h-32 bg-gradient-to-br ${preview.gradient} flex items-center justify-center relative`}
                    data-testid={`preview-${tmpl.id}`}
                  >
                    <span className={`text-3xl font-bold ${preview.dark ? 'text-gray-700' : 'text-white/80'}`}>
                      {preview.icon}
                    </span>
                    {/* Tag */}
                    <span className="absolute top-2 left-2 text-[10px] font-semibold bg-white/90 text-[#202124] px-1.5 py-0.5 rounded">
                      {preview.tag}
                    </span>
                    {/* Selected checkmark */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-[#1A73E8] rounded-full flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    {/* Preview hover */}
                    {isAccessible && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                        <div className="opacity-0 group-hover:opacity-100 bg-white/90 rounded-lg px-3 py-1.5 flex items-center gap-1.5 transition-opacity">
                          <Eye className="w-4 h-4 text-[#1A73E8]" />
                          <span className="text-xs font-semibold text-[#1A73E8]">Preview</span>
                        </div>
                      </div>
                    )}
                    {/* Lock for inaccessible */}
                    {!isAccessible && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <div className="bg-white/90 rounded-lg px-2 py-1 flex items-center gap-1">
                          <Lock className="w-3.5 h-3.5 text-[#5F6368]" />
                          <span className="text-xs font-medium text-[#5F6368]">Upgrade</span>
                        </div>
                      </div>
                    )}
                  </button>
                  {/* Info + Select button */}
                  <div className="p-2.5">
                    <p className="text-sm font-semibold text-[#202124] truncate">{tmpl.name}</p>
                    <p className="text-[10px] text-[#5F6368] line-clamp-1 mt-0.5 leading-tight">{tmpl.description}</p>
                    {isAccessible && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleTemplate(tmpl.id, isAccessible); }}
                        className={`mt-2 w-full py-1.5 rounded-md text-xs font-medium transition-colors ${
                          isSelected ? 'bg-[#1A73E8] text-white' : 'bg-[#F1F3F4] text-[#5F6368] hover:bg-[#E8F0FE] hover:text-[#1A73E8]'
                        }`}
                        data-testid={`select-${tmpl.id}`}
                      >
                        {isSelected ? 'Selected' : 'Select'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Upcoming More */}
          <div className="dashboard-card text-center py-6" data-testid="upcoming-more">
            <Sparkles className="w-8 h-8 text-[#DADCE0] mx-auto mb-2" />
            <p className="text-sm font-medium text-[#5F6368]">More templates coming soon!</p>
            <p className="text-xs text-[#9AA0A6] mt-1">We're designing new creative templates for your business.</p>
          </div>

          {/* Save Selection + Upgrade */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            {selectedIds.length < templates.filter(t => !t.is_accessible).length + selectedIds.length && planName !== 'pro' && (
              <Link to="/pricing" className="btn-outline text-sm flex items-center gap-1" data-testid="upgrade-templates-btn">
                <Crown className="w-4 h-4 text-[#F9AB00]" /> Upgrade for more templates
              </Link>
            )}
            <button
              onClick={handleSaveSelection}
              disabled={saving}
              className="btn-google flex items-center gap-2 ml-auto"
              data-testid="save-selection-btn"
            >
              {saving ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Saving...</> : <><Save className="w-4 h-4" /> Save Selection ({selectedIds.length}/{maxTemplates})</>}
            </button>
          </div>
        </>
      )}

      {/* Business Branding Tab */}
      {activeTab === 'branding' && (
        <div className="dashboard-card space-y-6" data-testid="branding-form">
          <div>
            <h3 className="text-base font-semibold text-[#202124] mb-1" style={{ fontFamily: 'Outfit' }}>Business Branding</h3>
            <p className="text-sm text-[#5F6368]">This information will be used in your creative templates.</p>
          </div>

          {/* Logo */}
          <div>
            <label className="block text-sm font-medium text-[#202124] mb-2">Business Logo</label>
            <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            <div className="flex items-center gap-4">
              {branding.logo_url ? (
                <img
                  src={branding.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : branding.logo_url}
                  alt="Logo"
                  className="w-20 h-20 object-contain rounded-xl border border-[#DADCE0] bg-white"
                />
              ) : (
                <div className="w-20 h-20 bg-[#F1F3F4] rounded-xl flex items-center justify-center border border-dashed border-[#DADCE0]">
                  <ImageIcon className="w-8 h-8 text-[#DADCE0]" />
                </div>
              )}
              <button
                onClick={() => logoInputRef.current?.click()}
                disabled={uploading}
                className="btn-outline text-sm"
                data-testid="upload-logo-btn"
              >
                <Upload className="w-4 h-4 inline mr-1" /> {uploading ? 'Uploading...' : 'Upload Logo'}
              </button>
            </div>
          </div>

          {/* Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#5F6368] mb-1"><Building2 className="w-3 h-3 inline mr-1" />Business Name</label>
              <input value={branding.business_name || ''} onChange={e => setBranding({ ...branding, business_name: e.target.value })} className="input-google" data-testid="branding-name" />
            </div>
            <div>
              <label className="block text-xs text-[#5F6368] mb-1"><Phone className="w-3 h-3 inline mr-1" />Phone Number</label>
              <input value={branding.phone || ''} onChange={e => setBranding({ ...branding, phone: e.target.value })} className="input-google" data-testid="branding-phone" />
            </div>
            <div>
              <label className="block text-xs text-[#5F6368] mb-1"><Globe className="w-3 h-3 inline mr-1" />Website</label>
              <input value={branding.website || ''} onChange={e => setBranding({ ...branding, website: e.target.value })} className="input-google" placeholder="https://..." data-testid="branding-website" />
            </div>
            <div>
              <label className="block text-xs text-[#5F6368] mb-1"><Mail className="w-3 h-3 inline mr-1" />Email</label>
              <input value={branding.email || ''} onChange={e => setBranding({ ...branding, email: e.target.value })} className="input-google" data-testid="branding-email" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-[#5F6368] mb-1"><MapPin className="w-3 h-3 inline mr-1" />Address</label>
              <input value={branding.address || ''} onChange={e => setBranding({ ...branding, address: e.target.value })} className="input-google" data-testid="branding-address" />
            </div>
          </div>

          {/* Social Links */}
          <div>
            <label className="block text-sm font-medium text-[#202124] mb-2">Social Media Links</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <Facebook className="w-4 h-4 text-[#1877F2] flex-shrink-0" />
                <input value={branding.social_facebook || ''} onChange={e => setBranding({ ...branding, social_facebook: e.target.value })} className="input-google text-sm" placeholder="Facebook URL" data-testid="branding-facebook" />
              </div>
              <div className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-[#E4405F] flex-shrink-0" />
                <input value={branding.social_instagram || ''} onChange={e => setBranding({ ...branding, social_instagram: e.target.value })} className="input-google text-sm" placeholder="Instagram URL" data-testid="branding-instagram" />
              </div>
              <div className="flex items-center gap-2">
                <Twitter className="w-4 h-4 text-[#1DA1F2] flex-shrink-0" />
                <input value={branding.social_twitter || ''} onChange={e => setBranding({ ...branding, social_twitter: e.target.value })} className="input-google text-sm" placeholder="Twitter / X URL" data-testid="branding-twitter" />
              </div>
              <div className="flex items-center gap-2">
                <Youtube className="w-4 h-4 text-[#FF0000] flex-shrink-0" />
                <input value={branding.social_youtube || ''} onChange={e => setBranding({ ...branding, social_youtube: e.target.value })} className="input-google text-sm" placeholder="YouTube URL" data-testid="branding-youtube" />
              </div>
            </div>
          </div>

          <div className="pt-2">
            <button onClick={handleSaveBranding} disabled={brandingLoading} className="btn-google flex items-center gap-2" data-testid="save-branding-btn">
              {brandingLoading ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Saving...</> : <><Save className="w-4 h-4" /> Save Branding</>}
            </button>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {previewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in-up" data-testid="preview-modal-overlay" onClick={() => setPreviewModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="preview-modal">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-[#DADCE0]">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${TEMPLATE_PREVIEWS[previewModal.id]?.gradient || 'from-gray-400 to-gray-600'} flex items-center justify-center`}>
                  <span className="text-sm font-bold text-white">{TEMPLATE_PREVIEWS[previewModal.id]?.icon || '?'}</span>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>{previewModal.name}</h3>
                  <span className="text-xs text-[#5F6368] capitalize">{previewModal.category} template</span>
                </div>
              </div>
              <button onClick={() => setPreviewModal(null)} className="w-8 h-8 rounded-full hover:bg-[#F1F3F4] flex items-center justify-center transition-colors" data-testid="preview-modal-close">
                <X className="w-4 h-4 text-[#5F6368]" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              <p className="text-sm text-[#5F6368]">{previewModal.description}</p>

              {/* Preview Image Area */}
              <div className="rounded-xl border border-[#DADCE0] overflow-hidden bg-[#F8F9FA] min-h-[200px] flex items-center justify-center" data-testid="preview-image-area">
                {previewImage ? (
                  <img src={previewImage} alt="Template preview" className="w-full h-auto object-contain" data-testid="preview-generated-image" />
                ) : previewGenerating ? (
                  <div className="flex flex-col items-center gap-3 py-10">
                    <div className="animate-spin w-8 h-8 border-3 border-[#1A73E8] border-t-transparent rounded-full" />
                    <p className="text-sm text-[#5F6368]">Generating preview with your branding...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 py-10 text-center px-6">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${TEMPLATE_PREVIEWS[previewModal.id]?.gradient || 'from-gray-400 to-gray-600'} flex items-center justify-center`}>
                      <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <p className="text-sm text-[#5F6368]">Click the button below to generate a branded preview using your business information.</p>
                  </div>
                )}
              </div>

              {/* Generate Preview Button */}
              {!previewImage && (
                <button
                  onClick={handleGeneratePreview}
                  disabled={previewGenerating}
                  className="w-full py-2.5 bg-gradient-to-r from-[#9334E9] to-[#1A73E8] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="generate-preview-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  {previewGenerating ? 'Generating...' : 'Generate Branded Preview'}
                </button>
              )}

              {/* Download button when image is ready */}
              {previewImage && (
                <a
                  href={previewImage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2.5 bg-[#F1F3F4] text-[#5F6368] rounded-lg text-sm font-medium hover:bg-[#E8EAED] transition-colors flex items-center justify-center gap-2"
                  data-testid="download-preview-btn"
                >
                  <Download className="w-4 h-4" /> View Full Image
                </a>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-[#DADCE0] flex gap-3">
              <button
                onClick={() => setPreviewModal(null)}
                className="flex-1 py-2.5 bg-[#F1F3F4] text-[#5F6368] rounded-lg text-sm font-medium hover:bg-[#E8EAED] transition-colors"
                data-testid="preview-cancel-btn"
              >
                Close
              </button>
              <button
                onClick={handleToggleFromModal}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  selectedIds.includes(previewModal.id)
                    ? 'bg-[#1A73E8] text-white hover:bg-[#1557B0]'
                    : 'bg-[#E8F0FE] text-[#1A73E8] hover:bg-[#D2E3FC]'
                }`}
                data-testid="preview-select-btn"
              >
                {selectedIds.includes(previewModal.id) ? <><Check className="w-4 h-4" /> Selected</> : <><Star className="w-4 h-4" /> Select Template</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativeTemplatePage;
